import { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { C } from '../styles';
import api from '../services/api';
import Dashboard from './Dashboard';
import { useAuth, AuthContext } from '../context/AuthContext';
import { TenantReportBuilder } from './ReportBuilder';
import { GlobalReportManager } from './GlobalReportManager';

// ── Superadmin SQL query (no tenant_id restriction)
const runSuperadminSqlQuery = (sql) =>
  api.post('/api/superadmin/sql-query', { sql });

const S = {
  page: { minHeight:'100vh', background:C.bgBase, fontFamily:"'DM Sans', sans-serif" },
  header: { background:C.bgCard, borderBottom:`1px solid ${C.border}`, padding:'0 32px', display:'flex', alignItems:'center', justifyContent:'space-between', height:'60px', position:'sticky', top:0, zIndex:100 },
  logo: { display:'flex', alignItems:'center', gap:'10px' },
  logoMark: { width:'32px', height:'32px', borderRadius:'50%', objectFit:'contain' },
  nav: { display:'flex', gap:'4px' },
  navBtn: (active) => ({ padding:'6px 16px', borderRadius:'8px', border:'none', background: active ? C.accentDim : 'transparent', color: active ? C.accent : C.textSec, fontSize:'13px', fontWeight: active ? '600' : '400', cursor:'pointer' }),
  body: { padding:'32px', maxWidth:'1400px', margin:'0 auto' },
  card: { background:C.bgSurface, borderRadius:'12px', border:`1px solid ${C.border}`, overflow:'hidden', marginBottom:'20px' },
  cardHeader: { padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: (sortable) => ({ padding:'10px 16px', textAlign:'left', fontSize:'18px', fontWeight:'600', color:C.textMuted, textTransform:'uppercase', letterSpacing:'0.5px', background:C.bgElevated, borderBottom:`1px solid ${C.border}`, cursor: sortable ? 'pointer' : 'default', userSelect:'none', whiteSpace:'nowrap' }),
  td: { padding:'12px 16px', fontSize:'16px', color:C.textPrimary, borderBottom:`1px solid ${C.border}` },
  badge: (active) => ({ padding:'2px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'600', background: active ? 'rgba(168,224,99,0.12)' : 'rgba(255,107,107,0.12)', color: active ? C.accent : C.danger }),
  btn: (variant='primary') => ({ padding:'6px 12px', borderRadius:'7px', border:'none', fontSize:'12px', fontWeight:'600', cursor:'pointer', background: variant==='primary' ? C.accent : variant==='danger' ? 'rgba(255,107,107,0.15)' : variant==='blue' ? 'rgba(96,165,250,0.15)' : variant==='amber' ? 'rgba(251,183,36,0.15)' : C.bgElevated, color: variant==='primary' ? '#000' : variant==='danger' ? C.danger : variant==='blue' ? C.blue : variant==='amber' ? C.amber : C.textSec }),
  modal: { position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 },
  modalBox: { background:C.bgSurface, borderRadius:'16px', border:`1px solid ${C.border}`, padding:'24px', width:'480px', boxShadow:'0 20px 60px rgba(0,0,0,0.5)', maxHeight:'90vh', overflowY:'auto' },
  input: { width:'100%', padding:'9px 12px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'13px', color:C.textPrimary, outline:'none', boxSizing:'border-box' },
  label: { fontSize:'11px', fontWeight:'600', color:C.textMuted, marginBottom:'5px', display:'block', textTransform:'uppercase', letterSpacing:'0.5px' },
  impersonateBanner: { background:'rgba(251,183,36,0.15)', border:`1px solid ${C.amber}`, padding:'8px 16px', display:'flex', justifyContent:'space-between', alignItems:'center' },
};

// ── Helpers
const Modal = ({ title, onClose, children, width='480px' }) => (
  <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{ ...S.modalBox, width }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0 }}>{title}</p>
        <button onClick={onClose} style={{ background:'none', border:'none', color:C.textMuted, fontSize:'20px', cursor:'pointer', lineHeight:1 }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, children, mb='14px' }) => (
  <div style={{ marginBottom:mb }}>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

const ROLE_COLORS = { boss: C.accent, area_manager: C.blue, branch_manager: C.purple, staff: C.amber };

// ── Sortable table hook
function useSorted(data, defaultCol='created_at', defaultDir='desc') {
  const [col, setCol]   = useState(defaultCol);
  const [dir, setDir]   = useState(defaultDir);

  const toggle = (c) => {
    if (c === col) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setCol(c); setDir('asc'); }
  };

  const sorted = [...data].sort((a, b) => {
    let av = a[col], bv = b[col];
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (typeof av === 'boolean') av = av ? 1 : 0;
    if (typeof bv === 'boolean') bv = bv ? 1 : 0;
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return dir === 'asc' ? cmp : -cmp;
  });

  const arrow = (c) => col === c ? (dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';
  return { sorted, toggle, arrow };
}

// ============================================================
// MERCHANTS TAB
// ============================================================
const MerchantsTab = () => {
  const [tenants, setTenants]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [showForm, setShowForm]             = useState(false);
  const [form, setForm]                     = useState({ tenant_id:'', tenant_name:'', contact_email:'' });
  const [error, setError]                   = useState('');
  const [editTenant, setEditTenant]         = useState(null);
  const [editForm, setEditForm]             = useState({ tenant_name:'', contact_email:'' });
  const [editError, setEditError]           = useState('');
  const [reportBuilderTenant, setRBTenant]  = useState(null); // { tenant_id, tenant_name }
  const [showGlobalReports,   setShowGlobal]= useState(false);
  const { sorted, toggle, arrow } = useSorted(tenants, 'created_at', 'desc');

  const load = async () => {
    try { const r = await api.get('/api/superadmin/tenants'); setTenants(r.data.data || []); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.tenant_id || !form.tenant_name) { setError('Merchant ID and name required'); return; }
    try { await api.post('/api/superadmin/tenants', form); setShowForm(false); setForm({ tenant_id:'', tenant_name:'', contact_email:'' }); setError(''); load(); }
    catch(e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const openEdit = (t) => {
    setEditTenant(t);
    setEditForm({ tenant_name: t.tenant_name || '', contact_email: t.contact_email || '' });
    setEditError('');
  };

  const submitEdit = async () => {
    if (!editForm.tenant_name) { setEditError('Name is required'); return; }
    try {
      await api.put(`/api/superadmin/tenants/${editTenant.tenant_id}`, editForm);
      setEditTenant(null); setEditError(''); load();
    } catch(e) { setEditError(e.response?.data?.error || 'Failed to update'); }
  };

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div>
          <p style={{ fontSize:'28px', fontWeight:'700', color:C.textPrimary, margin:0 }}>Merchants</p>
          <p style={{ fontSize:'16px', color:C.textMuted, margin:'2px 0 0' }}>{tenants.length} total</p>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button style={S.btn('secondary')} onClick={() => setShowGlobal(true)}>🌐 Global Reports</button>
          <button style={S.btn('primary')}   onClick={() => setShowForm(true)}>+ New Merchant</button>
        </div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              {[['tenant_id','Merchant ID'],['tenant_name','Name'],['contact_email','Email'],['boss_count','Boss Accounts'],['is_active','Status'],['created_at','Created']].map(([c,h]) => (
                <th key={c} style={S.th(true)} onClick={() => toggle(c)}>{h}{arrow(c)}</th>
              ))}
              <th style={S.th(false)}>Reports</th>
              <th style={S.th(false)}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{ ...S.td, textAlign:'center', color:C.textMuted }}>Loading...</td></tr>
            : sorted.length === 0 ? <tr><td colSpan={8} style={{ ...S.td, textAlign:'center', color:C.textMuted }}>No merchants yet</td></tr>
            : sorted.map(t => (
              <tr key={t.tenant_id} style={{ background:'transparent' }}>
                <td style={{ ...S.td, fontFamily:'monospace', color:C.accent }}>{t.tenant_id}</td>
                <td style={S.td}>
                  <span>{t.tenant_name}</span>
                  <button onClick={() => openEdit(t)} style={{ marginLeft:'8px', background:'transparent', border:'none', cursor:'pointer', fontSize:'12px', color:C.textMuted }}>✏️</button>
                </td>
                <td style={{ ...S.td, color:C.textSec }}>{t.contact_email || '-'}</td>
                <td style={{ ...S.td, color:C.textSec }}>{t.boss_count}</td>
                <td style={S.td}><span style={S.badge(t.is_active)}>{t.is_active ? 'Active' : 'Inactive'}</span></td>
                <td style={{ ...S.td, color:C.textMuted, fontSize:'16px' }}>{t.created_at ? String(t.created_at).substring(0,10) : '-'}</td>
                <td style={S.td}>
                  <button
                    style={S.btn('blue')}
                    onClick={() => setRBTenant({ tenant_id: t.tenant_id, tenant_name: t.tenant_name })}>
                    📊 Reports
                  </button>
                </td>
                <td style={S.td}>
                  <button style={S.btn(t.is_active ? 'danger' : 'secondary')}
                    onClick={async () => { await api.post(`/api/superadmin/tenants/${t.tenant_id}/toggle`); load(); }}>
                    {t.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editTenant && (
        <Modal title={`Edit — ${editTenant.tenant_id}`} onClose={() => { setEditTenant(null); setEditError(''); }}>
          <Field label="Business Name">
            <input style={S.input} value={editForm.tenant_name} onChange={e => setEditForm(f => ({ ...f, tenant_name: e.target.value }))} />
          </Field>
          <Field label="Contact Email">
            <input style={S.input} value={editForm.contact_email} onChange={e => setEditForm(f => ({ ...f, contact_email: e.target.value }))} />
          </Field>
          {editError && <p style={{ color:C.danger, fontSize:'16px', marginBottom:'12px' }}>{editError}</p>}
          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'4px' }}>
            <button style={S.btn('secondary')} onClick={() => { setEditTenant(null); setEditError(''); }}>Cancel</button>
            <button style={S.btn('primary')} onClick={submitEdit}>Save</button>
          </div>
        </Modal>
      )}

      {showForm && (
        <Modal title="New Merchant" onClose={() => { setShowForm(false); setError(''); }}>
          <Field label="Merchant ID (e.g. VD0150)">
            <input style={S.input} value={form.tenant_id} onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value.toUpperCase() }))} placeholder="VD0150" />
          </Field>
          <Field label="Business Name">
            <input style={S.input} value={form.tenant_name} onChange={e => setForm(f => ({ ...f, tenant_name: e.target.value }))} placeholder="Vital Design Salon" />
          </Field>
          <Field label="Contact Email (optional)">
            <input style={S.input} value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="owner@salon.com" />
          </Field>
          {error && <p style={{ color:C.danger, fontSize:'16px', marginBottom:'12px' }}>{error}</p>}
          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'4px' }}>
            <button style={S.btn('secondary')} onClick={() => { setShowForm(false); setError(''); }}>Cancel</button>
            <button style={S.btn('primary')} onClick={submit}>Create Merchant</button>
          </div>
        </Modal>
      )}

      {/* Report Builder overlay — full screen, per tenant */}
      {reportBuilderTenant && (
        <TenantReportBuilder
          tenant={reportBuilderTenant}
          onClose={() => setRBTenant(null)}
        />
      )}

      {/* Global Report Manager — full screen, cross-tenant */}
      {showGlobalReports && (
        <GlobalReportManager
          tenants={tenants}
          onClose={() => setShowGlobal(false)}
        />
      )}
    </>
  );
};

// ============================================================
// USERS TAB
// ============================================================
const UsersTab = ({ onImpersonate }) => {
  const [users, setUsers]         = useState([]);
  const [tenants, setTenants]     = useState([]);
  const [filterTenant, setFilter] = useState('');
  const [showArchived, setShowAr] = useState(false);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCr]   = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPass, setNewPass]     = useState('');
  const [error, setError]         = useState('');
  const [form, setForm] = useState({ tenant_id:'', full_name:'', username:'', password:'', role:'boss', staff_code:'', branch_ids:[] });
  const [editBranches,   setEditBranches]   = useState([]); // branches for edit modal
  const [createBranches, setCreateBranches] = useState([]); // branches for create modal // branches for the tenant being edited
  const { sorted, toggle, arrow } = useSorted(users, 'created_at', 'desc');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTenant) params.tenant_id = filterTenant;
      if (showArchived)  params.archived  = 'true';
      const [uR, tR] = await Promise.all([
        api.get('/api/superadmin/users', { params }),
        api.get('/api/superadmin/tenants'),
      ]);
      setUsers(uR.data.data || []);
      setTenants(tR.data.data || []);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterTenant, showArchived]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    for (const f of ['tenant_id','full_name','username','password']) {
      if (!form[f]) { setError(`${f} is required`); return; }
    }
    // Confirm staff_code accuracy when a staff code is provided
    if (form.staff_code && !window.confirm(
      `Please confirm: Staff Code "${form.staff_code}" matches the HR Code in the system exactly (case-sensitive). Proceed?`
    )) return;
    try {
      await api.post('/api/superadmin/users', form);
      setShowCr(false); setForm({ tenant_id:'', full_name:'', username:'', password:'', role:'boss', staff_code:'', branch_ids:[] }); setCreateBranches([]); setError(''); load();
    } catch(e) { setError(e.response?.data?.error || 'Failed'); }
  };

  const saveEdit = async () => {
    if (editUser.staff_code && !window.confirm(
      `Please confirm: Staff Code "${editUser.staff_code}" matches the HR Code in the system exactly (case-sensitive). Proceed?`
    )) return;
    try {
      const payload = {
        full_name:  editUser.full_name,
        username:   editUser.username,
        role:       editUser.role,
        tenant_id:  editUser.tenant_id,
        branch_ids: editUser.branch_ids || [],
        staff_code: editUser.staff_code || null,
      };
      if (editUser.newPassword) payload.password = editUser.newPassword;
      await api.put(`/api/superadmin/users/${editUser.user_id}`, payload);
      setEditUser(null); load();
    } catch(e) { alert(e.response?.data?.error || 'Failed to update'); }
  };

  const openEdit = async (u) => {
    // Load branches for this user's tenant before opening modal
    try {
      const r = await api.get('/api/team/branches', { params: { sa_tenant: u.tenant_id } });
      setEditBranches(r.data.data || []);
    } catch { setEditBranches([]); }
    setEditUser({ ...u, newPassword: '', branch_ids: u.branch_ids || [], staff_code: u.staff_code || '' });
  };

  // Load branches when tenant changes in the create modal
  useEffect(() => {
    if (!form.tenant_id) { setCreateBranches([]); return; }
    api.get('/api/team/branches', { params: { sa_tenant: form.tenant_id } })
      .then(r => setCreateBranches(r.data.data || []))
      .catch(() => setCreateBranches([]));
  }, [form.tenant_id]);

  const archive = async (u) => {
    if (!window.confirm(`Archive ${u.full_name}? They must be deactivated first.`)) return;
    try { await api.post(`/api/superadmin/users/${u.user_id}/archive`); load(); }
    catch(e) { alert(e.response?.data?.error || 'Failed'); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`DELETE ${u.full_name} (@${u.username})? This is permanent and cannot be undone.`)) return;
    if (!window.confirm(`Final confirmation: permanently delete "${u.full_name}"? All their data associations will be removed.`)) return;
    try { await api.delete(`/api/superadmin/users/${u.user_id}`); load(); }
    catch(e) { alert(e.response?.data?.error || 'Failed to delete user'); }
  };

  const unarchive = async (u) => {
    await api.post(`/api/superadmin/users/${u.user_id}/unarchive`); load();
  };

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div>
          <p style={{ fontSize:'28px', fontWeight:'700', color:C.textPrimary, margin:0 }}>{showArchived ? 'Archived Users' : 'Users'}</p>
          <p style={{ fontSize:'16px', color:C.textMuted, margin:'2px 0 0' }}>{users.length} accounts</p>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <select value={filterTenant} onChange={e => setFilter(e.target.value)} style={{ ...S.input, width:'200px' }}>
            <option value="">All Merchants</option>
            {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>)}
          </select>
          <button style={S.btn(showArchived ? 'amber' : 'secondary')} onClick={() => setShowAr(v => !v)}>
            {showArchived ? '← Active Users' : '🗄 Archived'}
          </button>
          {!showArchived && <button style={S.btn('primary')} onClick={() => setShowCr(true)}>+ New User</button>}
        </div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              {[['tenant_id','Merchant'],['full_name','Name'],['username','Username'],['role','Role'],['is_active','Status'],['created_at','Created']].map(([c,h]) => (
                <th key={c} style={S.th(true)} onClick={() => toggle(c)}>{h}{arrow(c)}</th>
              ))}
              <th style={S.th(false)}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} style={{ ...S.td, textAlign:'center', color:C.textMuted }}>Loading...</td></tr>
            : sorted.length === 0 ? <tr><td colSpan={7} style={{ ...S.td, textAlign:'center', color:C.textMuted }}>{showArchived ? 'No archived users' : 'No users found'}</td></tr>
            : sorted.map(u => (
              <tr key={u.user_id}>
                <td style={{ ...S.td, fontFamily:'monospace', fontSize:'16px', color:C.textSec }}>{u.tenant_id}</td>
                <td style={S.td}>{u.full_name}</td>
                <td style={{ ...S.td, color:C.textSec }}>{u.username}</td>
                <td style={S.td}>
                  <span style={{ padding:'2px 8px', borderRadius:'12px', fontSize:'12px', fontWeight:'600', background:`rgba(${u.role==='boss'?'168,224,99':u.role==='area_manager'?'96,165,250':'167,139,250'},0.12)`, color: ROLE_COLORS[u.role] || C.textSec }}>
                    {u.role}
                  </span>
                </td>
                <td style={S.td}><span style={S.badge(u.is_active)}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                <td style={{ ...S.td, color:C.textMuted, fontSize:'16px' }}>{u.created_at ? String(u.created_at).substring(0,10) : '-'}</td>
                <td style={S.td}>
                  <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                    {showArchived ? (
                      <div style={{ display:'flex', gap:'5px' }}>
                        <button style={S.btn('primary')} onClick={() => unarchive(u)}>Unarchive</button>
                        <button style={S.btn('danger')}  onClick={() => deleteUser(u)}>Delete</button>
                      </div>
                    ) : (
                      <>
                        <button style={S.btn('secondary')} onClick={() => openEdit(u)}>Edit</button>
                        <button style={S.btn('secondary')} onClick={() => { setResetUser(u); setNewPass(''); }}>Reset PW</button>
                        <button style={S.btn(u.is_active ? 'danger' : 'secondary')}
                          onClick={async () => { await api.post(`/api/superadmin/users/${u.user_id}/toggle`); load(); }}>
                          {u.is_active ? 'Disable' : 'Enable'}
                        </button>
                        {!u.is_active && (
                          <>
                            <button style={S.btn('amber')} onClick={() => archive(u)}>Archive</button>
                            <button style={S.btn('danger')} onClick={() => deleteUser(u)}>Delete</button>
                          </>
                        )}
                        {u.role === 'boss' && (
                          <button style={S.btn('blue')} onClick={() => onImpersonate(u)}>Access</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create user modal */}
      {showCreate && (
        <Modal title="New User" onClose={() => { setShowCr(false); setError(''); }}>
          <Field label="Merchant"><select style={S.input} value={form.tenant_id} onChange={e => setForm(f => ({ ...f, tenant_id: e.target.value }))}>
            <option value="">Select merchant...</option>
            {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>)}
          </select></Field>
          <Field label="Full Name"><input style={S.input} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></Field>
          <Field label="Username"><input style={S.input} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} /></Field>
          <Field label="Password"><input style={S.input} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></Field>
          <Field label="Role"><select style={S.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
            <option value="boss">Boss (Salon Owner)</option>
            <option value="area_manager">Area Manager</option>
            <option value="branch_manager">Branch Manager</option>
            <option value="staff">Staff</option>
          </select></Field>
          {form.role !== 'boss' && (
            <Field label="Staff Code (must match HR Code exactly)">
              <input style={S.input} value={form.staff_code}
                onChange={e => setForm(f => ({ ...f, staff_code: e.target.value.trim() }))}
                placeholder="e.g. EV01" />
              {form.staff_code && (
                <p style={{ fontSize:'12px', color:C.amber, margin:'6px 0 0', display:'flex', alignItems:'center', gap:'5px' }}>
                  ⚠ Ensure this matches exactly as the HR Code for this staff.
                </p>
              )}
            </Field>
          )}
          {form.role !== 'boss' && (
            <Field label="Branches">
              {!form.tenant_id ? (
                <p style={{ fontSize:'12px', color:C.textMuted, margin:0 }}>Select a merchant first.</p>
              ) : createBranches.length === 0 ? (
                <p style={{ fontSize:'12px', color:C.textMuted, margin:0 }}>No branches found for this merchant.</p>
              ) : (
                <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                  {createBranches.map(b => {
                    const sel = (form.branch_ids || []).includes(b.branch_code);
                    return (
                      <button key={b.branch_code}
                        onClick={() => setForm(f => ({
                          ...f,
                          branch_ids: sel
                            ? f.branch_ids.filter(x => x !== b.branch_code)
                            : [...(f.branch_ids || []), b.branch_code]
                        }))}
                        style={{ padding:'5px 12px', borderRadius:'20px', fontSize:'12px', cursor:'pointer',
                          border:`1px solid ${sel ? C.accent : C.border}`,
                          background: sel ? C.accentDim : 'transparent',
                          color:      sel ? C.accent    : C.textSec,
                          fontWeight: sel ? '600' : '400' }}>
                        {b.branch_name || b.branch_code}
                      </button>
                    );
                  })}
                </div>
              )}
              {(form.branch_ids || []).length > 0 && (
                <p style={{ fontSize:'11px', color:C.textMuted, margin:'6px 0 0' }}>
                  Selected: {form.branch_ids.join(', ')}
                </p>
              )}
            </Field>
          )}
          {error && <p style={{ color:C.danger, fontSize:'16px', marginBottom:'12px' }}>{error}</p>}
          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'4px' }}>
            <button style={S.btn('secondary')} onClick={() => { setShowCr(false); setError(''); }}>Cancel</button>
            <button style={S.btn('primary')} onClick={submit}>Create User</button>
          </div>
        </Modal>
      )}

      {/* Edit user modal */}
      {editUser && (
        <Modal title={`Edit: ${editUser.username}`} onClose={() => setEditUser(null)}>
          <Field label="Merchant ID">
            <select style={S.input} value={editUser.tenant_id} onChange={e => setEditUser(u => ({ ...u, tenant_id: e.target.value }))}>
              {tenants.map(t => <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name} ({t.tenant_id})</option>)}
            </select>
          </Field>
          <Field label="Full Name"><input style={S.input} value={editUser.full_name} onChange={e => setEditUser(u => ({ ...u, full_name: e.target.value }))} /></Field>
          <Field label="Username"><input style={S.input} value={editUser.username} onChange={e => setEditUser(u => ({ ...u, username: e.target.value }))} /></Field>
          <Field label="Role"><select style={S.input} value={editUser.role} onChange={e => setEditUser(u => ({ ...u, role: e.target.value }))}>
            <option value="boss">Boss</option>
            <option value="area_manager">Area Manager</option>
            <option value="branch_manager">Branch Manager</option>
            <option value="staff">Staff</option>
          </select></Field>
          {editUser.role !== 'boss' && (
            <Field label="Staff Code (must match HR Code exactly)">
              <input style={S.input} value={editUser.staff_code || ''}
                onChange={e => setEditUser(u => ({ ...u, staff_code: e.target.value.trim() }))}
                placeholder="e.g. EV01" />
              {editUser.staff_code && (
                <p style={{ fontSize:'12px', color:C.amber, margin:'6px 0 0', display:'flex', alignItems:'center', gap:'5px' }}>
                  ⚠ Ensure this matches the HR Code exactly — it is case-sensitive.
                </p>
              )}
            </Field>
          )}
          <Field label="Branches">
            {editBranches.length === 0 ? (
              <p style={{ fontSize:'12px', color:C.textMuted, margin:0 }}>No branches found for this tenant.</p>
            ) : (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                {editBranches.map(b => {
                  const sel = (editUser.branch_ids || []).includes(b.branch_code);
                  return (
                    <button key={b.branch_code}
                      onClick={() => setEditUser(u => ({
                        ...u,
                        branch_ids: sel
                          ? u.branch_ids.filter(x => x !== b.branch_code)
                          : [...(u.branch_ids || []), b.branch_code]
                      }))}
                      style={{ padding:'5px 12px', borderRadius:'20px', fontSize:'12px', cursor:'pointer',
                        border:`1px solid ${sel ? C.accent : C.border}`,
                        background: sel ? C.accentDim : 'transparent',
                        color:      sel ? C.accent    : C.textSec,
                        fontWeight: sel ? '600' : '400' }}>
                      {b.branch_name || b.branch_code}
                    </button>
                  );
                })}
              </div>
            )}
            {(editUser.branch_ids || []).length > 0 && (
              <p style={{ fontSize:'11px', color:C.textMuted, margin:'6px 0 0' }}>
                Selected: {editUser.branch_ids.join(', ')}
              </p>
            )}
          </Field>
          <Field label="New Password (optional)">
            <input style={S.input} type="password" value={editUser.newPassword} onChange={e => setEditUser(u => ({ ...u, newPassword: e.target.value }))} placeholder="Leave blank to keep the same" />
          </Field>
          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'4px' }}>
            <button style={S.btn('secondary')} onClick={() => setEditUser(null)}>Cancel</button>
            <button style={S.btn('primary')} onClick={saveEdit}>Save Changes</button>
          </div>
        </Modal>
      )}

      {/* Reset password modal */}
      {resetUser && (
        <Modal title={`Reset Password — ${resetUser.username}`} onClose={() => setResetUser(null)}>
          <Field label="New Password">
            <input style={S.input} type="password" value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Enter new password" />
          </Field>
          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'4px' }}>
            <button style={S.btn('secondary')} onClick={() => setResetUser(null)}>Cancel</button>
            <button style={S.btn('primary')} onClick={async () => { await api.post(`/api/superadmin/users/${resetUser.user_id}/reset-password`, { password: newPass }); setResetUser(null); }}>Reset</button>
          </div>
        </Modal>
      )}
    </>
  );
};

// ============================================================
// MAIN SUPERADMIN PANEL
// ============================================================
const SuperadminPanel = ({ onLogout }) => {
  const [tab, setTab]               = useState('merchants');
  const [impersonating, setImp]     = useState(null); // { token, full_name, tenant_id }
  const { login: authLogin, user }  = useAuth();

  const handleImpersonate = async (bossUser) => {
    try {
      const res = await api.post(`/api/superadmin/impersonate/${bossUser.user_id}`);
      setImp({
        token:      res.data.token,
        full_name:  res.data.full_name,
        tenant_id:  res.data.tenant_id,
        user_id:    res.data.user_id,
        role:       res.data.role,
        branch_ids: res.data.branch_ids,
        impersonated_by: res.data.impersonated_by,
      });
    } catch(e) { alert('Failed to access account'); }
  };

  // While impersonating: render boss dashboard with fake auth context
  if (impersonating) {
    return (
      <ImpersonatedSession
        impersonating={impersonating}
        onReturn={() => setImp(null)}
      />
    );
  }

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={S.logo}>
          <img src="/logo512.png" alt="CCS" style={S.logoMark} />
          <div>
            <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0 }}>CCS BI Dashboard</p>
            <p style={{ fontSize:'12px', color:C.textMuted, margin:0 }}>Superadmin Console</p>
          </div>
        </div>
        <div style={S.nav}>
          <button style={S.navBtn(tab==='merchants')} onClick={() => setTab('merchants')}>Merchants</button>
          <button style={S.navBtn(tab==='users')} onClick={() => setTab('users')}>Users</button>
          <button style={S.navBtn(tab==='etl')} onClick={() => setTab('etl')}>⚙ ETL</button>
          <button style={S.navBtn(tab==='sql')} onClick={() => setTab('sql')}>🛢 SQL</button>
        </div>
        <button style={{ ...S.btn('secondary'), fontSize:'12px' }} onClick={onLogout}>Log Out</button>
      </div>
      <div style={S.body}>
        {tab === 'merchants' && <MerchantsTab />}
        {tab === 'users'     && <UsersTab onImpersonate={handleImpersonate} />}
        {tab === 'etl'       && <EtlTab />}
        {tab === 'sql'       && <SqlTab />}
      </div>
    </div>
  );
};

// Override AuthContext for the impersonated session - no page reload needed
const ImpersonatedAuthContext = createContext(null);

const ImpersonatedSession = ({ impersonating, onReturn }) => {
  // Swap the token in localStorage so api.js interceptor uses boss token
  useEffect(() => {
    const prev = localStorage.getItem('token');
    localStorage.setItem('token', impersonating.token);
    return () => {
      // Restore SA token when unmounted (Return clicked)
      if (prev) localStorage.setItem('token', prev);
    };
  }, [impersonating.token]);

  // Fake user object that Dashboard/tabs will read via useAuth()
  const fakeUser = {
    user_id:    impersonating.user_id,
    full_name:  impersonating.full_name,
    role:       impersonating.role,
    tenant_id:  impersonating.tenant_id,
    branch_ids: impersonating.branch_ids,
  };

  const fakeAuth = {
    user:    fakeUser,
    loading: false,
    logout:  onReturn, // Return to superadmin on logout click
    login:   async () => {},
  };

  return (
    <AuthContext.Provider value={fakeAuth}>
      {/* Yellow banner */}
      <div style={{ background:'rgba(251,183,36,0.15)', borderBottom:`1px solid ${C.amber}`, padding:'8px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, zIndex:999 }}>
        <span style={{ fontSize:'16px', color:C.amber, fontWeight:'600' }}>
          Viewing as: {impersonating.full_name} · {impersonating.tenant_id}
        </span>
        <button
          onClick={onReturn}
          style={{ padding:'5px 14px', background:'rgba(251,183,36,0.2)', color:C.amber, border:`2px solid ${C.amber}`, borderRadius:'7px', fontSize:'16px', fontWeight:'600', cursor:'pointer' }}>
          ← Return to Superadmin
        </button>
      </div>
      <DashboardWithContext />
    </AuthContext.Provider>
  );
};

// Dashboard that reads from ImpersonatedAuthContext if available
const DashboardWithContext = () => {
  // Lazy import to avoid circular dependency
  const DashEl = require('./Dashboard').default;
  return <DashEl />;
};

// ============================================================
// SQL TOOL TAB
// ============================================================
const downloadCsv = (columns, allData) => {
  let csv = columns.map(c => `"${c}"`).join(',') + '\n';
  allData.forEach(row => {
    csv += row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',') + '\n';
  });
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `sa_query_${Date.now()}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ============================================================
// ETL TAB
// ============================================================
const EtlTab = () => {
  const [files, setFiles]           = useState([]);
  const [filesLoading, setFL]       = useState(true);
  const [running, setRunning]       = useState(false);
  const [streamLines, setStreamLines] = useState([]); // live log lines
  const [etlStatus, setEtlStatus]   = useState(null); // 'success'|'error'|null
  const streamBoxRef                = useRef(null);
  const esRef                       = useRef(null);
  const doneRef                     = useRef(false); // sync flag — avoids React async state race

  const API_BASE = process.env.REACT_APP_API_URL || 'https://bi.salonsoftware.my';

  const loadFiles = async () => {
    setFL(true);
    try { const r = await api.get('/api/superadmin/etl/files'); setFiles(r.data.files || []); }
    catch { /* silent */ } finally { setFL(false); }
  };

  useEffect(() => { loadFiles(); }, []);

  // Auto-scroll stream box to bottom as lines arrive
  useEffect(() => {
    if (streamBoxRef.current) {
      streamBoxRef.current.scrollTop = streamBoxRef.current.scrollHeight;
    }
  }, [streamLines]);

  // Cleanup SSE on unmount
  useEffect(() => () => { if (esRef.current) esRef.current.close(); }, []);

    const handleRun = () => {
    if (!window.confirm('Run ETL? This will process all pending uploads.')) return;

    // Reset state
    doneRef.current = false;
    setRunning(true);
    setStreamLines([]);
    setEtlStatus(null);

    // Close any existing connection
    if (esRef.current) esRef.current.close();

    const token = localStorage.getItem('token');
    const url   = `${API_BASE}/api/superadmin/etl/stream?token=${encodeURIComponent(token)}`;
    const es    = new EventSource(url);
    esRef.current = es;

    // Heartbeat: show "still running..." every 10s while no new log lines arrive
    let lastLineTime = Date.now();
    const heartbeatInterval = setInterval(() => {
      const elapsed = Math.round((Date.now() - lastLineTime) / 1000);
      if (elapsed >= 10) {
        setStreamLines(prev => [...prev, `⏳ Still running... (${elapsed}s since last log)`]);
        lastLineTime = Date.now();
      }
    }, 10000);

    es.onmessage = (e) => {
      lastLineTime = Date.now();
      if (e.data === '__ping__') return; // backend keepalive — ignore
      const line = e.data;
      setStreamLines(prev => [...prev, line]);
    };

    es.addEventListener('done', (e) => {
      clearInterval(heartbeatInterval);
      doneRef.current = true; // set sync BEFORE es.close() triggers onerror
      const status = e.data.startsWith('success') ? 'success' : 'error';
      setEtlStatus(status);
      setRunning(false);
      es.close();
      loadFiles();
    });

    es.onerror = () => {
      clearInterval(heartbeatInterval);
      if (doneRef.current) return; // done event already received — ignore this onerror
      setEtlStatus('error');
      setRunning(false);
      es.close();
    };
  };

  const lineColor = (line) => {
    if (/error|fail|exception/i.test(line))       return '#ff6b6b';
    if (/success|complete|upserted|provisioned|refreshed/i.test(line)) return '#a8e063';
    if (/warn|skip|empty/i.test(line))            return '#fbb724';
    return C.textSec;
  };

  return (
    <div style={{ padding: '20px' }}>

      {/* Run ETL */}
      <div style={{ background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px', marginBottom: '20px' }}>
        <p style={{ fontSize: '14px', fontWeight: '700', color: C.textPrimary, margin: '0 0 4px' }}>Run ETL Manually</p>
        <p style={{ fontSize: '12px', color: C.textMuted, margin: '0 0 14px' }}>Processes all pending zip files from the upload folder into PostgreSQL.</p>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={handleRun} disabled={running}
            style={{ ...S.btn('primary'), opacity: running ? 0.6 : 1, cursor: running ? 'not-allowed' : 'pointer' }}>
            {running ? '⚙ Running...' : '▶ Run ETL'}
          </button>
          {running && (
            <span style={{ fontSize: '12px', color: C.textMuted, fontStyle: 'italic' }}>
              Processing — logs streaming live below...
            </span>
          )}
          <button onClick={() => { loadFiles(); }} style={{ ...S.btn('secondary'), marginLeft: 'auto' }}>↻ Refresh</button>
        </div>

        {/* Live stream terminal */}
        {(streamLines.length > 0 || etlStatus) && (
          <div style={{ marginTop: '16px', borderRadius: '10px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
            {/* Terminal header */}
            <div style={{ background: '#1a1d27', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }} />
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#fbb724', display: 'inline-block' }} />
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#a8e063', display: 'inline-block' }} />
              </div>
              <span style={{ fontSize: '11px', color: C.textMuted, fontFamily: 'monospace' }}>
                etl_excel.py — live output
              </span>
              {etlStatus && (
                <span style={{ fontSize: '12px', fontWeight: '700',
                  color: etlStatus === 'success' ? '#a8e063' : '#ff6b6b' }}>
                  {etlStatus === 'success' ? '✓ Completed' : '✗ Failed'}
                </span>
              )}
            </div>
            {/* Log lines */}
            <div ref={streamBoxRef} style={{
              background: '#0d1117', padding: '12px 16px',
              maxHeight: '320px', overflowY: 'auto',
              fontFamily: 'monospace', fontSize: '12px', lineHeight: '1.8',
            }}>
              {streamLines.map((line, i) => (
                <div key={i} style={{ color: lineColor(line) }}>
                  <span style={{ color: '#4b5563', marginRight: '10px', userSelect: 'none' }}>›</span>
                  {line}
                </div>
              ))}
              {running && (
                <div style={{ color: C.textMuted, marginTop: '4px' }}>
                  <span style={{ color: '#4b5563', marginRight: '10px' }}>›</span>
                  <span style={{ animation: 'blink 1s step-end infinite' }}>▌</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Uploaded Files */}
      {(
        <div style={{ background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: '12px', overflow: 'hidden' }}>
          {filesLoading
            ? <p style={{ padding: '24px', textAlign: 'center', color: C.textMuted }}>Loading...</p>
            : files.length === 0
              ? <p style={{ padding: '24px', textAlign: 'center', color: C.textMuted }}>No zip files in upload folder.</p>
              : (
                <table style={S.table}>
                  <thead><tr>
                    {['Tenant', 'Type', 'Filename', 'Size', 'Uploaded'].map(h => <th key={h} style={S.th(false)}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {files.map((f, i) => (
                      <tr key={i}>
                        <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: '700', color: C.accent }}>{f.tenant_id}</td>
                        <td style={S.td}>
                          <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                            background: f.type === 'Daily' ? 'rgba(96,165,250,0.15)' : 'rgba(168,224,99,0.15)',
                            color: f.type === 'Daily' ? C.blue : C.accent }}>
                            {f.type}
                          </span>
                        </td>
                        <td style={{ ...S.td, fontSize: '12px', fontFamily: 'monospace', color: C.textSec }}>{f.filename}</td>
                        <td style={{ ...S.td, color: C.textMuted }}>{f.size_kb} KB</td>
                        <td style={{ ...S.td, color: C.textMuted }}>{f.uploaded}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
          }
        </div>
      )}

    </div>
  );
};


const SqlTab = () => {
  const [sql, setSql]       = useState('');
  const [running, setRun]   = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError]   = useState('');

  const run = async () => {
    if (!sql.trim()) return;
    setRun(true); setResult(null); setError('');
    try {
      const res = await runSuperadminSqlQuery(sql);
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response ? `HTTP ${err.response.status}` : err.message)
        || 'Query failed.';
      setError(msg);
    } finally { setRun(false); }
  };

  const showing = result?.display_data || [];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:'20px' }}>
        <p style={{ fontSize:'20px', fontWeight:'700', color:C.textPrimary, margin:'0 0 4px' }}>SQL Query Tool</p>
        <p style={{ fontSize:'13px', color:C.textMuted, margin:0 }}>
          SELECT only · No tenant restriction · password_hash always hidden · Displays first 100 rows · CSV exports all
        </p>
      </div>

      {/* Editor card */}
      <div style={{ background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px', padding:'20px', marginBottom:'20px' }}>
        <label style={S.label}>Query</label>
        <textarea
          value={sql}
          onChange={e => setSql(e.target.value)}
          placeholder={
            'SELECT t.tenant_id, t.tenant_name, COUNT(s.id) AS total_sales\n' +
            'FROM tenants t\n' +
            'LEFT JOIN sales s ON s.tenant_id = t.tenant_id\n' +
            'GROUP BY t.tenant_id, t.tenant_name\n' +
            'ORDER BY total_sales DESC'
          }
          style={{
            width: '100%', minHeight: '140px', padding: '12px 14px',
            fontFamily: 'monospace', fontSize: '13px',
            background: C.bgElevated, border: `1px solid ${C.border}`,
            borderRadius: '8px', outline: 'none', resize: 'vertical',
            boxSizing: 'border-box', color: C.textPrimary,
          }}
        />
        <button
          onClick={run}
          disabled={running || !sql.trim()}
          style={{
            marginTop: '12px', padding: '10px 24px',
            background: running || !sql.trim() ? C.bgElevated : C.accent,
            color: running || !sql.trim() ? C.textMuted : '#000',
            border: 'none', borderRadius: '8px',
            fontSize: '14px', fontWeight: '700', cursor: running || !sql.trim() ? 'not-allowed' : 'pointer',
          }}>
          {running ? 'Running...' : '▶  Run Query'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background:'rgba(255,107,107,0.1)', border:`1px solid ${C.danger}`, borderRadius:'8px', padding:'12px 16px', marginBottom:'20px', color:C.danger, fontSize:'13px' }}>
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div style={{ background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px', overflow:'hidden' }}>
          <div style={{ padding:'14px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <p style={{ fontSize:'14px', fontWeight:'700', color:C.textPrimary, margin:0 }}>Results</p>
              <p style={{ fontSize:'12px', color:C.textMuted, margin:'2px 0 0' }}>
                Showing {showing.length} of {result.total_rows} row{result.total_rows !== 1 ? 's' : ''}
                {result.capped && <span style={{ color:C.amber }}> · capped at 10,000</span>}
              </p>
            </div>
            <button
              onClick={() => downloadCsv(result.columns, result.all_data)}
              style={{ padding:'7px 14px', background:C.accentDim, border:`1px solid ${C.accent}`, borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor:'pointer', color:C.accent }}>
              ⬇ CSV ({result.total_rows})
            </button>
          </div>

          {result.total_rows === 0
            ? <p style={{ textAlign:'center', color:C.textMuted, fontSize:'13px', padding:'32px' }}>No rows returned.</p>
            : (
              <div style={{ overflowX:'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      {result.columns.map(c => (
                        <th key={c} style={S.th(false)}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {showing.map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : C.bgElevated }}>
                        {row.map((cell, j) => (
                          <td key={j} style={S.td}>
                            {cell === null
                              ? <span style={{ color:C.textMuted, fontStyle:'italic' }}>null</span>
                              : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}
    </div>
  );
};

export default SuperadminPanel;